import { Component, OnInit, inject } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { AuthService } from "../../../services/api/auth/auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import {
  hasLowerCaseValidator,
  hasNumberValidator,
  hasUpperCaseValidator,
  lengthBetween6And64Validator,
  passwordCriteriaTexts,
} from "../../validators";
import { SnackbarService } from "../../../services/snackbar/snackbar.service";
import { ThemeService } from "../../../services/theme/theme.service";

@Component({
  selector: "app-signup",
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: "./signup.component.html",
  styleUrl: "./signup.component.scss",
})
export class SignupComponent implements OnInit {
  signupForm: FormGroup;
  errorMessage: string = "";
  showPassword = false;
  showConfirmedPassword = false;
  passwordControl: AbstractControl | null = null;
  passwordCriteria = passwordCriteriaTexts;
  private snackService = inject(SnackbarService);
  themeService = inject(ThemeService);
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.signupForm = this.fb.group(
      {
        firstName: [
          "",
          [Validators.required, Validators.pattern("^[a-zA-Z ]+$")],
        ],
        lastName: [
          "",
          [Validators.required, Validators.pattern("^[a-zA-Z ]+$")],
        ],
        email: ["", [Validators.required, Validators.email]],
        password: [
          "",
          [
            Validators.required,
            lengthBetween6And64Validator,
            hasUpperCaseValidator,
            hasLowerCaseValidator,
            hasNumberValidator,
          ],
        ],
        confirmedPassword: ["", Validators.required],
        invitationCode: [
          "",
          // [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
        ],
        phone: [null],
      },
      { validators: this.confirmPasswordMatchValidator },
    );
    this.passwordControl = this.signupForm.get("password");
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const invitationCode = params.get("invitationCode");
      if (invitationCode) {
        this.signupForm.patchValue({ invitationCode });
      }
    });
  }

  confirmPasswordMatchValidator = (control: AbstractControl) => {
    const passwordControl = control.get("password");
    const confirmedPasswordControl = control.get("confirmedPassword");
    if (!passwordControl || !confirmedPasswordControl) {
      return null; // If either control is missing, do not validate
    }
    if (passwordControl.value !== confirmedPasswordControl.value) {
      return { passwordMismatch: true };
    }
    return null;
  };

  getInputErrorMessage(controlName: string) {
    // Special handling for confirmedPassword to check against password
    if (controlName === "password") return null; // Skip password field error messages here to be handled differently
    if (controlName === "confirmedPassword") {
      const confirmedPasswordControl = this.signupForm.get("confirmedPassword");
      if (confirmedPasswordControl === null) return null;
      if (confirmedPasswordControl.dirty || confirmedPasswordControl.touched) {
        if (
          confirmedPasswordControl.hasError("required") &&
          confirmedPasswordControl.invalid
        ) {
          return `Field is required`;
        }
        if (this.signupForm.hasError("passwordMismatch")) {
          return "Passwords do not match";
        }
      }
      return null;
    }
    if (controlName === "invitationCode") {
      const invitationCodeControl = this.signupForm.get("invitationCode");
      if (invitationCodeControl === null) return null;
      if (invitationCodeControl.dirty || invitationCodeControl.touched) {
        if (
          invitationCodeControl.hasError("required") &&
          invitationCodeControl.invalid
        ) {
          return `Field is required`;
        }
        if (
          invitationCodeControl.hasError("minlength") ||
          invitationCodeControl.hasError("maxlength")
        ) {
          return "Invitation code must be 6 characters";
        }
      }
      return null;
    }

    const control = this.signupForm.get(controlName);
    if (control === null) return null;
    if (control.invalid && (control.dirty || control.touched)) {
      if (control.hasError("required")) {
        return `Field is required`;
      }
      if (control.hasError("email")) {
        return "Not a valid email";
      }

      if (control.hasError("minlength")) {
        const minLength = control.getError("minlength")?.requiredLength || 0;
        return `Minimum length is ${minLength}`;
      }
      if (control.hasError("maxlength")) {
        const maxLength = control.getError("maxlength")?.requiredLength || 0;
        return `Maximum length is ${maxLength}`;
      }

      if (control.hasError("pattern")) {
        return `Letters and spaces only`;
      }
    }
    return null;
  }

  getInputVerificationCodeErrorMessage() {
    return null;
  }

  signUp(): void {
    if (this.signupForm.invalid) {
      this.signupForm.markAllAsTouched();
      return;
    }
    const { firstName, lastName, email, password, phone, invitationCode } =
      this.signupForm.value;
    const normalizedEmail = email.trim().toLowerCase();
    const signUpDto = {
      first_name: firstName,
      last_name: lastName,
      email: normalizedEmail,
      password: password,
      role: "GROWER",
      phone: phone || undefined,
      invitationCode: invitationCode?.trim(),
    };
    this.authService.signUp(signUpDto).subscribe({
      next: (response) => {
        this.errorMessage = "";
        this.snackService.show({
          message: "Account created. You can sign in now.",
          duration: 5000,
        });
        this.directTo();
      },
      error: (err) => {
        this.errorMessage = err.message || "An error occurred during sign up";
        this.snackService.show({
          message: this.errorMessage,
          duration: 5000,
        });
        console.error("Login error", err);
      },
    });
  }

  directTo(route?: string): void {
    this.router.navigate([route ?? "login"], { replaceUrl: true });
  }
}
